import { ReactNode } from "react";
import t from "@/lang/lang";
import {
  BuildingOrderBy,
  BuildingWithoutAreas,
} from "@/services/backend/buildings/types/building";
import {
  ListBuildingRequest,
  useBuildingsListQuery,
} from "@/services/backend/buildings/api/building-api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { SearchFilter } from "@/shared/components/filter/search-filter";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { formatDateTime } from "@/shared/lib/utilities/date";
import { BuildingAddressFilter } from "@/services/backend/buildings/components/building-address-filter";

export function BuildingsTable({
  request,
  onRequestChange,
  renderBuildingLink,
  renderAddressLink,
  createAction,
}: {
  request: ListBuildingRequest;
  onRequestChange: (request: ListBuildingRequest) => void;
  renderBuildingLink: (building: BuildingWithoutAreas) => ReactNode;
  renderAddressLink: (building: BuildingWithoutAreas) => ReactNode;
  createAction?: ReactNode;
}) {
  const {
    data: list,
    isLoading,
    error,
  } = useBuildingsListQuery(request, {
    refetchOnMountOrArgChange: true,
  });

  const emptyList = !isLoading && !error && !!list && list.items.length === 0;

  return (
    <div className="grid gap-2">
      <BuildingsTableFilters
        buildings={list?.items}
        request={request}
        onRequestChange={onRequestChange}
        createAction={createAction}
      />
      <TableCard>
        <Table>
          <BuildingsTableHeader />
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={4} />}
            {!!error && <TableRtkErrorRow error={error} colSpan={4} />}
            {emptyList && <TableEmptyRow colSpan={4} />}
            {list?.items.map((building) => (
              <TableRow key={building.id}>
                <TableCell>{renderBuildingLink(building)}</TableCell>
                <TableCell>{renderAddressLink(building)}</TableCell>
                <TableCell>{formatDateTime(building.createdAt)}</TableCell>
                <TableCell>{formatDateTime(building.updatedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>
      {list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) =>
            onRequestChange({ ...request, limit, offset })
          }
          className="ml-auto"
        />
      )}
    </div>
  );
}

function BuildingsTableHeader() {
  return (
    <TableHeader>
      <TableRow>
        <TableHead>{t("Name")} </TableHead>
        <TableHead>{t("Adresse")}</TableHead>
        <TableHead>{t("Erstellt am")}</TableHead>
        <TableHead>{t("Aktualisiert am")}</TableHead>
      </TableRow>
    </TableHeader>
  );
}

function BuildingsTableFilters({
  buildings,
  request,
  onRequestChange,
  createAction,
}: {
  buildings?: BuildingWithoutAreas[];
  request: ListBuildingRequest;
  onRequestChange: (request: ListBuildingRequest) => void;
  createAction?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <SearchFilter
          filter={request.search ?? { active: false, values: [] }}
          onFilterChange={(search) =>
            onRequestChange({ ...request, search, offset: 0 })
          }
          placeholder={t("Nach Name oder Adresse suchen ...")}
          className="h-8 w-full sm:w-72"
        />
        <BuildingAddressFilter
          buildings={buildings}
          filter={request.address}
          onFilterChange={(address) =>
            onRequestChange({ ...request, address, offset: 0 })
          }
        />
        <OrderBySelect<BuildingOrderBy>
          orderBy={request.orderBy}
          onOrderByChange={(orderBy) =>
            onRequestChange({ ...request, orderBy })
          }
          fields={[
            { label: t("Name"), value: "name" },
            { label: t("Erstellt am"), value: "createdAt" },
            { label: t("Aktualisiert am"), value: "updatedAt" },
          ]}
        />
      </div>
      {createAction}
    </div>
  );
}
